import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { suppliersApi } from '../api';
import toast from 'react-hot-toast';
import './SuppliersDetails.css';

export default function SuppliersDetails() {
  const { id } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSupplier();
  }, [id]);

  const fetchSupplier = async () => {
    try {
      const res = await suppliersApi.getById(id);
      console.log('API Response:', res);
      console.log('res.data:', res?.data);
      // Handle both response formats: { data: supplier } and { data: { supplier } }
      if (res?.data?.data?.supplier) {
        setSupplier(res.data.data.supplier);
      } else if (res?.data?.data) {
        setSupplier(res.data.data);
      } else if (res?.data) {
        setSupplier(res.data);
      }
    } catch (err) {
      console.error('Error fetching supplier:', err);
      toast.error(err.response?.data?.message || 'Failed to load supplier');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">🏭</div>
          <p>Supplier not found</p>
          <Link to="/suppliers" className="btn btn-primary">Back to Suppliers</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container supplier-details-page">
      {/* Header */}
      <div className="page-header">
        <div className="supplier-header-wrap">
          <Link to="/suppliers" className="btn btn-secondary btn-sm back-btn">← Back</Link>
          <div className="supplier-title-wrap">
            <h1 className="page-title">{supplier.name}</h1>
            <span className={`badge badge-${supplier.status}`}>{supplier.status}</span>
          </div>
        </div>
        <div className="supplier-actions">
          <Link to={`/suppliers?edit=${supplier.id}`} className="btn btn-secondary btn-sm">
            Edit Supplier
          </Link>
        </div>
      </div>

      <div className="supplier-grid">
        {/* Left Column */}
        <div className="supplier-left-col">
          {/* Supplier Info Card */}
          <div className="card">
            <h3 className="card-title">Supplier Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Supplier Name</span>
                <span className="info-value font-semibold">{supplier.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status</span>
                <span className={`info-value badge badge-${supplier.status}`}>{supplier.status}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                <span className="info-value">{supplier.phone}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{supplier.email || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">GST Number</span>
                <span className="info-value mono">{supplier.gstNumber || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Created Date</span>
                <span className="info-value">{formatDate(supplier.createdAt)}</span>
              </div>
              {supplier.updatedAt && (
                <div className="info-item">
                  <span className="info-label">Last Updated</span>
                  <span className="info-value">{formatDate(supplier.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Address Card */}
          <div className="card">
            <h3 className="card-title">Address Details</h3>
            {supplier.address ? (
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <span className="info-label">Address</span>
                <span className="info-value" style={{ whiteSpace: 'pre-wrap' }}>{supplier.address}</span>
              </div>
            ) : (
              <div className="empty-state">
                <p className="text-muted">No address provided</p>
              </div>
            )}
          </div>

          {/* Notes Card */}
          {supplier.notes && (
            <div className="card">
              <h3 className="card-title">Notes</h3>
              <p className="info-value text-sm" style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{supplier.notes}</p>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="supplier-right-col">
          {/* Quick Stats Card */}
          <div className="card">
            <h3 className="card-title">Quick Information</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Supplier ID</span>
                <span className="mono text-muted">#{supplier.id}</span>
              </div>
              <div className="summary-row">
                <span>Account Status</span>
                <span className={`badge badge-${supplier.status}`}>{supplier.status}</span>
              </div>
              <div className="summary-row">
                <span>GST Verified</span>
                <span>{supplier.gstNumber ? '✓ Yes' : '—'}</span>
              </div>
              <div className="summary-row">
                <span>Email Verified</span>
                <span>{supplier.email ? '✓ Yes' : '—'}</span>
              </div>
              <div className="summary-row total">
                <span>Member Since</span>
                <span className="font-bold">{formatDate(supplier.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Contact Actions Card */}
          <div className="card">
            <h3 className="card-title">Contact Actions</h3>
            <div className="contact-actions">
              {supplier.phone && (
                <a href={`tel:${supplier.phone}`} className="btn btn-secondary">
                  📞 Call {supplier.phone}
                </a>
              )}
              {supplier.email && (
                <a href={`mailto:${supplier.email}`} className="btn btn-secondary">
                  ✉️ Email {supplier.email}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
